export const ensureQuestionIds = (questions = []) =>
  (Array.isArray(questions) ? questions : []).map((question, index) => ({
    ...question,
    id: question?.id || question?.questionId || `q-${index + 1}`,
  }));

export const imageJobStatus = (question) => {
  if (!question) return "idle";
  if (question.generatingVisual) return "loading";
  if (question.imageCancelled) return "cancelled";
  if (question.imageError) return "failed";
  if (question.imageUrl || question.questionImgName) return "success";
  return "idle";
};

/** Starts image jobs for the given ids. A job that already succeeded or is loading is not started again. */
export const beginImageJobs = (questions = [], ids = [], now = Date.now()) => {
  const wanted = new Set(ids);
  return ensureQuestionIds(questions).map((question) => {
    if (!wanted.has(question.id)) return { ...question, generatingVisual: false };
    const status = imageJobStatus(question);
    if (status === "success" || status === "loading") return question;
    return {
      ...question,
      generatingVisual: true,
      imageError: "",
      imageCancelled: false,
      imageStartedAt: now,
      visualStatus: "Pending",
    };
  });
};

export const finishImageJob = (questions = [], id, result = {}) =>
  ensureQuestionIds(questions).map((question) => {
    if (question.id !== id) return question;
    if (result.cancelled) {
      return { ...question, generatingVisual: false, imageCancelled: true, imageError: "" };
    }
    if (!result.ok) {
      return {
        ...question,
        generatingVisual: false,
        imageError: result.error || "Unable to add this visual.",
        visualStatus: "Failed",
        imageUrl: "",
        questionImgName: null,
      };
    }
    return {
      ...question,
      generatingVisual: false,
      imageError: "",
      imageCancelled: false,
      needsImage: true,
      visualStatus: "Generated",
      imageUrl: result.imageUrl || "",
      questionImgName: result.questionImgName || null,
      altText: result.altText || question.altText || "",
    };
  });

/** Applies image fields by question id. Successful jobs are left as they are. */
export const applyImageResponse = (questions = [], returned = []) => {
  const byId = new Map(
    (Array.isArray(returned) ? returned : []).filter((question) => question?.id).map((question) => [question.id, question])
  );
  return ensureQuestionIds(questions).map((question) => {
    if (imageJobStatus(question) === "success" && !question.generatingVisual) return question;
    const next = byId.get(question.id) || null;
    if (!question.generatingVisual) return question;
    const url = next?.imageUrl || "";
    const name = next?.questionImgName || "";
    if (url || name) {
      return finishImageJob([question], question.id, {
        ok: true,
        imageUrl: url,
        questionImgName: name,
        altText: next?.altText || "",
      })[0];
    }
    return finishImageJob([question], question.id, {
      ok: false,
      error: next?.imageError || "Unable to add this visual.",
    })[0];
  });
};

export const expireImageJobs = (questions = [], now = Date.now(), timeoutMs = 45000) =>
  ensureQuestionIds(questions).map((question) => {
    if (!question.generatingVisual || !question.imageStartedAt) return question;
    if (now - question.imageStartedAt < timeoutMs) return question;
    return finishImageJob([question], question.id, { ok: false, error: "Image generation timed out." })[0];
  });

export const cancelImageJobs = (questions = []) =>
  ensureQuestionIds(questions).map((question) =>
    question.generatingVisual ? finishImageJob([question], question.id, { cancelled: true })[0] : question
  );
